import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaArrowLeft, FaUserGraduate, FaBuilding, FaIdBadge, 
  FaCalendarAlt, FaUsers, FaMoneyBillWave, FaReceipt, 
  FaFileInvoiceDollar, FaRegMoneyBillAlt, FaHistory, FaFilePdf
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api, { BASE_URL } from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import './StudentData.css';

const API_BASE = BASE_URL;

const StudentData = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [student, setStudent] = useState(null);
  const [payments, setPayments] = useState([]);
  const [transports, setTransports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTransportId, setExpandedTransportId] = useState(null);

  // ── Fetch Student & Payments Data ──
  useEffect(() => {
    if (!id) return;
    setLoading(true);

    Promise.allSettled([
      api.get(`/students/${id}`),
      api.get('/fees/allocations', { params: { student: id, limit: 100 } }),
      api.get('/transport/allocations', { params: { student: id, all: true } }),
      api.get('/payments', { params: { student: id, limit: 100 } }),
    ])
      .then(([studentRes, paymentRes, transRes, rawPayRes]) => {
        if (studentRes.status === 'rejected') {
          Swal.fire('Error', 'Failed to load student profile.', 'error');
          navigate(-1);
          return;
        }
        setStudent(studentRes.value.data.data);
        setPayments(paymentRes.status === 'fulfilled' ? (paymentRes.value.data.data || []) : []);
        const rawPays = rawPayRes.status === 'fulfilled' ? (rawPayRes.value.data.data || []) : [];
        const sorted = (transRes.status === 'fulfilled' ? (transRes.value.data.data || []) : []).map(t => {
          const linked = rawPays.find(p => String(p.transportAllocation) === String(t._id));
          return { ...t, _linkedPayment: linked || null };
        }).sort((a, b) => {
          const ya = parseInt((a.session?.name || '').match(/\d{4}/)?.[0] || 0);
          const yb = parseInt((b.session?.name || '').match(/\d{4}/)?.[0] || 0);
          return ya - yb;
        });
        setTransports(sorted);
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // ── Financial Calculations ──
  const { totalBilled, totalPaid, balanceDue, totalTransactions } = useMemo(() => {
    let billed = 0, paid = 0, balance = 0, txns = 0;
    payments.forEach(p => {
      billed  += (p.totalAmount || p.amountDue  || 0);
      paid    += (p.paidAmount  || p.amountPaid || 0);
      balance += (p.dueAmount   || p.balance    || 0);
      txns    += (p.transactions?.length || 0);
    });
    return { totalBilled: billed, totalPaid: paid, balanceDue: balance, totalTransactions: txns };
  }, [payments]);

  // Navigate to FeeCollectionLog — use role-based path
  const handleViewLog = (payment) => {
    const role = user?.role;
    const basePath = role === 'collegeadmin' ? '/collegeadmin' : role === 'clerk' ? '/clerk' : '/admin';
    navigate(`${basePath}/fee-collection-log`, {
      state: {
        allocation: {
          ...payment,
          college: payment.college || student?.college || {},
          student: payment.student || student || {},
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="student-data-and-structure-loader-container">
        <div className="student-data-and-structure-spinner"></div>
        <p>Loading Profile...</p>
      </div>
    );
  }

  if (!student) return null;

  const photoUrl = student.photo ? `${API_BASE}/${student.photo}` : null;
  const batch       = student.admissionYear || '—';
  const currentYear = student.currentYear   || '—';

  return (
    <div className="student-data-and-structure-container">
      
      {/* ── Header ── */}
      <div className="student-data-and-structure-header">
        <button type="button" className="student-data-and-structure-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <h2 className="student-data-and-structure-title">Student Profile</h2>
      </div>

      {/* ── 1. Top Profile Card ── */}
      <div className="student-data-and-structure-profile-card">
        
        {/* College Name Banner */}
        <div className="student-data-and-structure-college-banner">
          <FaBuilding className="student-data-and-structure-college-icon" />
          <span>{student.college?.name || '—'}</span>
        </div>

        <div className="student-data-and-structure-profile-body">
          {/* Photo (Exactly 150x150) */}
          <div className="student-data-and-structure-photo-wrapper">
            {photoUrl ? (
              <img 
                src={photoUrl} 
                alt={student.name} 
                className="student-data-and-structure-photo" 
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <div className="student-data-and-structure-photo-fallback" style={{ display: photoUrl ? 'none' : 'flex' }}>
              <FaUserGraduate />
            </div>
          </div>

          {/* Key Details */}
          <div className="student-data-and-structure-primary-info">
            <h1 className="student-data-and-structure-name">{student.name}</h1>
            <div className="student-data-and-structure-badges-row">
              <span className="student-data-and-structure-badge"><FaIdBadge /> {student.admissionNo || '—'}</span>
              <span className="student-data-and-structure-badge-session">Session: {student.session?.name || '—'}</span>
              <span className="student-data-and-structure-badge-batch">Batch: {batch}</span>
              <span className="student-data-and-structure-badge-batch">Year: {currentYear}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── 2. Financial Summary Grid ── */}
      <div className="student-data-and-structure-finance-grid">
        <div className="student-data-and-structure-finance-card billed">
          <div className="student-data-and-structure-finance-icon"><FaFileInvoiceDollar /></div>
          <div className="student-data-and-structure-finance-text">
            <label>Total Billed</label>
            <span>₹ {totalBilled.toLocaleString()}</span>
          </div>
        </div>
        <div className="student-data-and-structure-finance-card paid">
          <div className="student-data-and-structure-finance-icon"><FaMoneyBillWave /></div>
          <div className="student-data-and-structure-finance-text">
            <label>Total Paid</label>
            <span>₹ {totalPaid.toLocaleString()}</span>
          </div>
        </div>
        <div className="student-data-and-structure-finance-card balance">
          <div className="student-data-and-structure-finance-icon"><FaRegMoneyBillAlt /></div>
          <div className="student-data-and-structure-finance-text">
            <label>Balance Due</label>
            <span>₹ {balanceDue.toLocaleString()}</span>
          </div>
        </div>
        <div className="student-data-and-structure-finance-card count">
          <div className="student-data-and-structure-finance-icon"><FaReceipt /></div>
          <div className="student-data-and-structure-finance-text">
            <label>Transactions</label>
            <span>{totalTransactions} Transaction{totalTransactions !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* ── 3. Detailed Information (3 Columns) ── */}
      <div className="student-data-and-structure-info-grid">
        
        {/* Personal Info */}
        <div className="student-data-and-structure-info-column">
          <div className="student-data-and-structure-column-header">
            <FaUserGraduate /> Personal Information
          </div>
          <div className="student-data-and-structure-info-list">
            <div className="student-data-and-structure-info-item">
              <label>Date of Birth</label>
              <span>{student.dob ? new Date(student.dob).toLocaleDateString('en-GB') : '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Gender</label>
              <span>{student.gender || '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Category / Religion</label>
              <span>{student.category || '—'} / {student.religion || '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Contact</label>
              <span>{student.contact || student.phone || '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Email</label>
              <span>{student.email || '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Address</label>
              <span>{student.address || '—'}, {student.city || '—'} ({student.state || '—'})</span>
            </div>
          </div>
        </div>

        {/* Academic Info */}
        <div className="student-data-and-structure-info-column">
          <div className="student-data-and-structure-column-header">
            <FaBuilding /> Academic Information
          </div>
          <div className="student-data-and-structure-info-list">
            <div className="student-data-and-structure-info-item">
              <label>Class</label>
              <span className="student-data-and-structure-highlight-text">{student.class?.name || student.className || '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Batch</label>
              <span className="student-data-and-structure-highlight-text">{batch}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Year</label>
              <span className="student-data-and-structure-highlight-text">{currentYear}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Medium</label>
              <span>{student.class?.medium || student.medium || '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Admission Date</label>
              <span>{student.admissionDate ? new Date(student.admissionDate).toLocaleDateString('en-GB') : '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Last School</label>
              <span>{student.lastSchoolName || '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Last Class Attended</label>
              <span>{student.lastSchoolClass || '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Reason for Leaving</label>
              <span>{student.leftReason || '—'}</span>
            </div>
          </div>
        </div>

        {/* Parent Info */}
        <div className="student-data-and-structure-info-column">
          <div className="student-data-and-structure-column-header">
            <FaUsers /> Parent Information
          </div>
          <div className="student-data-and-structure-info-list">
            <div className="student-data-and-structure-info-item">
              <label>Father's Name</label>
              <span className="student-data-and-structure-highlight-text">{student.fatherName || '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Father's Contact</label>
              <span>{student.fatherContact || '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Father's Occupation</label>
              <span>{student.fatherOccupation || '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Mother's Name</label>
              <span className="student-data-and-structure-highlight-text">{student.motherName || '—'}</span>
            </div>
            <div className="student-data-and-structure-info-item">
              <label>Mother's Occupation</label>
              <span>{student.motherOccupation || '—'}</span>
            </div>
          </div>
        </div>

        {/* Education Document */}
        <div className="student-data-and-structure-info-column">
          <div className="student-data-and-structure-column-header">
            <FaFilePdf /> Education Document
          </div>
          <div className="student-data-and-structure-info-list">
            <div className="student-data-and-structure-info-item">
              <label>Document</label>
              {student.educationDocument ? (
                <button
                  onClick={() => window.open(`${API_BASE}/${student.educationDocument}`, '_blank')}
                  style={{
                    background: '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#2563eb'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#3b82f6'}
                >
                  <FaFilePdf /> View Document
                </button>
              ) : (
                <span style={{ color: '#94a3b8', fontSize: '0.88rem' }}>Not Uploaded</span>
              )}
            </div>
          </div>
        </div>

        {/* Transport Info — full-width table like session history */}
        <div className="student-data-and-structure-info-column" style={{ gridColumn: '1 / -1' }}>
          <div className="student-data-and-structure-column-header">
            🚌 Transport History
          </div>
          {transports.length === 0 ? (
            <div className="student-data-and-structure-info-item" style={{ padding: '12px 0' }}>
              <span style={{ color: '#94a3b8', fontSize: '0.88rem' }}>No transport allocated</span>
            </div>
          ) : (
            <div className="student-data-and-structure-table-wrapper" style={{ marginTop: 8 }}>
              <table className="student-data-and-structure-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>S.No</th>
                    <th>Session</th>
                    <th>Bus No.</th>
                    <th>Driver</th>
                    <th>Route</th>
                    <th>Stop</th>
                    <th>Fees (₹)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transports.map((t, i) => {
                    const joinDate   = t.joinDate;
                    const endDate    = t.endDate;
                    const cancelDate = t.cancelDate;
                    // Duration for display only (cancelled status)
                    const msPerDay   = 1000 * 60 * 60 * 24;
                    const daysUsed   = (joinDate && cancelDate)
                      ? Math.ceil((new Date(cancelDate) - new Date(joinDate)) / msPerDay)
                      : 0;
                    const months     = Math.floor(daysUsed / 30);
                    const remDays    = daysUsed % 30;
                    const isExpanded = expandedTransportId === t._id;

                    return (
                      <React.Fragment key={t._id || i}>
                        <tr>
                          <td style={{ textAlign: 'center', cursor: 'pointer', fontSize: '1.2rem' }} onClick={() => setExpandedTransportId(isExpanded ? null : t._id)}>
                            {isExpanded ? '▼' : '▶'}
                          </td>
                          <td>{i + 1}</td>
                          <td><span className="sfc-badge-soft">{t.session?.name || '—'}</span></td>
                          <td><strong style={{ color: '#1e40af' }}>{t.bus?.busNumber || '—'}</strong></td>
                          <td style={{ fontSize: '0.83rem', color: '#475569' }}>
                            {t.bus?.driverName || '—'}
                            {t.bus?.driverPhone ? <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{t.bus.driverPhone}</div> : null}
                          </td>
                          <td style={{ color: '#64748b' }}>{t.route?.name || '—'}</td>
                          <td style={{ fontWeight: 600 }}>{t.stop || '—'}</td>
                          <td><strong style={{ color: '#10b981' }}>₹ {t.price || 0}</strong></td>
                          <td>
                            {t.status === 'pending_review' ? (
                              <span style={{ background: '#fef3c7', color: '#d97706', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>
                                ⚠ Pending
                              </span>
                            ) : t.status === 'completed' ? (
                              <span style={{ background: '#f3e8ff', color: '#7c3aed', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #e9d5ff', whiteSpace: 'nowrap' }}>
                                ✓ Completed
                              </span>
                            ) : t.status === 'cancelled' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                                <span style={{ background: '#fef2f2', color: '#ef4444', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #fecaca', whiteSpace: 'nowrap' }}>
                                  ✕ Cancelled
                                </span>
                                {cancelDate && (
                                  <span style={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 600 }}>
                                    {new Date(cancelDate).toLocaleDateString('en-GB')}
                                  </span>
                                )}
                              </div>
                            ) : t.status === 'inactive' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                                <span style={{ background: '#f1f5f9', color: '#64748b', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                                  Inactive
                                </span>
                                {t._linkedPayment?.status === 'cancelled' && (
                                  <span style={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 600 }}>
                                    Fee cancelled
                                    {t._linkedPayment.cancelledAt
                                      ? ` · ${new Date(t._linkedPayment.cancelledAt).toLocaleDateString('en-GB')}`
                                      : ''}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span style={{ background: '#dcfce7', color: '#16a34a', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #bbf7d0', whiteSpace: 'nowrap' }}>
                                Active
                              </span>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                            <td colSpan="9" style={{ padding: '16px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                                
                                {/* For Completed Status - Show minimal info */}
                                {t.status === 'completed' && (
                                  <>
                                    <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '3px solid #8b5cf6' }}>
                                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Status</div>
                                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#8b5cf6' }}>
                                        ✓ Completed
                                      </div>
                                    </div>
                                    <div style={{ gridColumn: '1 / -1', background: '#f3f4f6', padding: '12px', borderRadius: '6px', borderLeft: '3px solid #9ca3af' }}>
                                      <div style={{ fontSize: '0.85rem', color: '#4b5563' }}>
                                        This allocation has been promoted to next session and is now marked as completed.
                                      </div>
                                    </div>
                                  </>
                                )}

                                {/* For Inactive Status - Show only status */}
                                {t.status === 'inactive' && (
                                  <>
                                    <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '3px solid #64748b' }}>
                                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Status</div>
                                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#64748b' }}>
                                        ◌ Inactive
                                      </div>
                                    </div>
                                  </>
                                )}

                                {/* For Cancelled Status */}
                                {t.status === 'cancelled' && (
                                  <>
                                    <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '3px solid #2563eb' }}>
                                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Join Date</div>
                                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>{joinDate ? new Date(joinDate).toLocaleDateString('en-GB') : '—'}</div>
                                    </div>
                                    <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '3px solid #7c3aed' }}>
                                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>End Date</div>
                                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>{endDate ? new Date(endDate).toLocaleDateString('en-GB') : '—'}</div>
                                    </div>
                                    <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '3px solid #dc2626' }}>
                                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Cancel Date</div>
                                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#dc2626' }}>{cancelDate ? new Date(cancelDate).toLocaleDateString('en-GB') : '—'}</div>
                                    </div>
                                    <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '3px solid #f59e0b' }}>
                                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Duration Used</div>
                                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#d97706' }}>
                                        {months > 0 ? `${months} month${months > 1 ? 's' : ''} ` : ''}{remDays > 0 ? `${remDays} day${remDays > 1 ? 's' : ''}` : ''}{months === 0 && remDays === 0 ? '< 1 day' : ''}
                                      </div>
                                    </div>
                                  </>
                                )}

                                {/* For Active Status - Show only status, join date, and end date */}
                                {t.status === 'active' && (
                                  <>
                                    {/* Join Date */}
                                    <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '3px solid #2563eb' }}>
                                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Join Date</div>
                                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>
                                        {joinDate ? new Date(joinDate).toLocaleDateString('en-GB') : '—'}
                                      </div>
                                    </div>

                                    {/* End Date */}
                                    <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '3px solid #7c3aed' }}>
                                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>End Date</div>
                                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>
                                        {endDate ? new Date(endDate).toLocaleDateString('en-GB') : '—'}
                                      </div>
                                    </div>
                                  </>
                                )}

                                {/* For Pending Status */}
                                {t.status === 'pending_review' && (
                                  <>
                                    <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '3px solid #2563eb' }}>
                                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Join Date</div>
                                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>{joinDate ? new Date(joinDate).toLocaleDateString('en-GB') : '—'}</div>
                                    </div>
                                    <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', borderLeft: '3px solid #7c3aed' }}>
                                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>End Date</div>
                                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>{endDate ? new Date(endDate).toLocaleDateString('en-GB') : '—'}</div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      {/* ── 4. Session History ── */}
      <div className="student-data-and-structure-table-container" style={{ marginBottom: '20px' }}>
        <h3 className="student-data-and-structure-table-title"><FaCalendarAlt style={{ marginRight: 8 }} />Session / Promotion History</h3>
        <div className="student-data-and-structure-table-wrapper">
          <table className="student-data-and-structure-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Session</th>
                <th>Year</th>
                <th>Class</th>
                <th>Promoted On</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(student.sessionHistory || []).map((h, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{h.sessionName || h.session?.name || '—'}</td>
                  <td>{h.year || '—'}</td>
                <td>{h.className || h.class?.name || '—'}</td>
                  <td>{h.promotedAt ? new Date(h.promotedAt).toLocaleDateString('en-GB') : '—'}</td>
                  <td>
                    <span style={{ background: '#dcfce7', color: '#16a34a', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #bbf7d0' }}>
                      Promoted
                    </span>
                  </td>
                </tr>
              ))}
              {/* Current session row — always shown */}
              <tr style={{ background: '#f0fdf4', fontWeight: 600 }}>
                <td>{(student.sessionHistory?.length || 0) + 1}</td>
                <td>{student.session?.name || '—'}</td>
                <td>{student.currentYear || '—'}</td>
                <td>{student.class?.name || student.className || '—'}</td>
                <td><span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Current</span></td>
                <td>
                  {student.currentYear === 'Passout' ? (
                    <span style={{ background: '#f3e8ff', color: '#7c3aed', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #e9d5ff' }}>
                      Passout
                    </span>
                  ) : (
                    <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #bfdbfe' }}>
                      Active
                    </span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── 5. Fees Payment History Table ── */}
      <div className="student-data-and-structure-table-container">
        <h3 className="student-data-and-structure-table-title">💰 Fees Payment History</h3>
        <div className="student-data-and-structure-table-wrapper">
          <table className="student-data-and-structure-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Session</th>
                <th style={{ textAlign: 'left' }}>Fee Name</th>
                <th>Total Fee</th>
                <th>Paid Fee</th>
                <th>Late Fee</th>
                <th>Remaining Fee</th>
                <th>Status</th>
                <th>Fee Due Date</th>
                <th>Transactions</th>
                <th>Log</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td colSpan="11" className="student-data-and-structure-empty">No payment records found.</td>
                </tr>
              ) : payments.map((payment, idx) => {
                const txnCount = payment.transactions?.length || 0;
                return (
                  <tr key={payment._id}>
                    <td>{idx + 1}</td>
                    <td><span className="sfc-badge-soft">{payment.session || '—'}</span></td>
                    <td className="student-data-and-structure-fee-type" style={{ textAlign: 'left' }}>
                      {payment.feeName || '—'}
                    </td>
                    <td className="student-data-and-structure-amount">₹ {(payment.totalAmount || 0).toLocaleString()}</td>
                    <td className="student-data-and-structure-paid">₹ {(payment.paidAmount || 0).toLocaleString()}</td>
                    <td className="student-data-and-structure-latefee">₹ {(payment.lateFee || 0).toLocaleString()}</td>
                    <td className="student-data-and-structure-balance">₹ {(payment.dueAmount || 0).toLocaleString()}</td>
                    <td>
                      <span className={`student-data-and-structure-status ${payment.status}`}>
                        {payment.status}
                      </span>
                    </td>
                    <td className="student-data-and-structure-date">
                      {payment.feeStructure?.paymentEnd
                        ? new Date(payment.feeStructure.paymentEnd).toLocaleDateString('en-GB')
                        : '—'}
                    </td>
                    <td>
                      {txnCount > 0
                        ? <span className="sds-txn-count">{txnCount}</span>
                        : <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                    <td>
                      {txnCount > 0 ? (
                        <button
                          className="sds-log-btn"
                          onClick={() => handleViewLog(payment)}
                          title="View Payment Log"
                        >
                          <FaHistory /> Log
                        </button>
                      ) : <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default StudentData;